import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { apiGet, apiDelete, apiPost, apiPut } from '../../generic/Api_Functions'
import { Box, Chip, CircularProgress, FormHelperText, IconButton, Skeleton, Stack } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { AddCircleRounded, TextSnippetOutlined, UploadFile } from '@mui/icons-material'
import Document from '../document/Document'
import moment from 'moment/moment'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel, systemValueOptionText } from '../../generic/systemValues/systemValueLabel'
import LineOrder from './LineOrder'
import ServiceForm from './ServiceForm'
import useToggleSmartAccordionVisibility from '../../generic/hooks/useToggleSmartAccordionVisibility/useToggleSmartAccordionVisibility'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import useGetExtendedColumns from '../../generic/hooks/useGetExtendedColumns/useGetExtendedColumns'
import GeneralRequestForm from './GeneralRequestForm'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import FeedIcon from '@mui/icons-material/Feed'
import { isOrderStatusAllowed } from '../../generic/utilities/statusPermissionGating'
import MainImageThumbnail, { COLUMN_WIDTH as MAIN_IMAGE_COLUMN_WIDTH, THUMBNAIL_WIDTH as MAIN_IMAGE_THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT as MAIN_IMAGE_THUMBNAIL_HEIGHT } from '../parts/MainImageThumbnail'
import useMainImageResolution from '../parts/useMainImageResolution'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import QrCodeGeneratorDialog, { buildAvailableFields } from '../../generic/qr/QrCodeGeneratorDialog'
import useRepositorySelection from '../../generic/hooks/useRepositorySelection'
import { parentPreselectedRecordFrom, childPreselectedRecordFrom, childPreselectedRecordForRow } from '../../generic/utilities/repositorySelectionMapping'
import SensitiveBoundary from '../../vision/SensitiveBoundary'
import { useProtectedRowPlaceholder, useVisionCoversSensitive } from '../../vision/protectedRow'
// Import typedefs from another file
/**
 * @typedef {import('../../generic/utilities/objectStructures/orderWindow/order').Order} Order Order object
 * @typedef {import('../../generic/utilities/objectStructures/orderWindow/order').DialogStatus} DialogStatus DialogStatus object
 */

const Order = (props) => {
  const [editing, setEditing] = useState(false)
  const [showDocument, setShowDocument] = useState(false)
  const [showServiceForm, setShowServiceForm] = useState(false)
  const [showQr, setShowQr] = useState(false)
  const [qrRow, setQrRow] = useState(null)
  const [orderRow, setOrderRow] = useState(null)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const { t_ } = useSmartTranslation()
  // Story 213 S302 (C15.6): while it covers, the whole row of a protected order is the blurred placeholder.
  const protectedRowPlaceholder = useProtectedRowPlaceholder()
  const visionCoversSensitive = useVisionCoversSensitive()
  const smartAccordionVisibility = useToggleSmartAccordionVisibility()
  // Historia 170 (J4b, S89/S103): ORDER -> (child) ORDER_LINE, rendered as `LineOrder.jsx` inside this
  // row's own accordion (not a dialog). `onPreselectionResolved` opens that accordion automatically
  // ONLY when there is a child selection to hand it — same rule as every other nested window.
  const repositorySelection = useRepositorySelection()
  const handleOrderPreselectionResolved = (row) => {
    if (row && childPreselectedRecordFrom(repositorySelection)) {
      smartAccordionVisibility.setAccordion(row.id, true, false)
    }
  }
  const [editIndex, setEditIndex] = useState(-2)
  const isUserAuthorized = useAuthorization()
  const extendedColumns = useGetExtendedColumns('order')
  const [forOrder, setForOrder] = useState(null)
  const [openForm, setOpenForm] = useState(false)
  const [showServiceFormButton, setShowServiceFormButton] = useState(false)
  // The backend-filtered catalog of order statuses the profile owns (the same `simpleOrderStatuses`
  // the status dropdown consumes, S24) — never dynamic permission codes read as authorities on the
  // front.
  //
  // Revision of historia 40 (#2819): NOT owning a row's status no longer blocks the record. An order
  // in a status the profile does not own (e.g. FINISHED) stays fully editable and deletable; the only
  // thing locked is the `status` field itself, which turns read-only for that row. Every other
  // restriction (EDIT_ORDERS / DELETE_ORDERS / ADD_ORDERS_LINES, and the server-side filtering of the
  // status catalog) is unchanged.
  const [allowedOrderStatuses, setAllowedOrderStatuses] = useState([])
  // Main image is resolved per visible page from the single-line orders' part reference id,
  // reusing the same batch resolver as LineOrder. Merge semantics + a resolved-id cache: paging
  // back never re-requests references already resolved (only new ids hit the batch endpoint).
  const [mainImageInfoMap, , mergeMainImages, mainImageThumbnailMap] = useMainImageResolution()
  // Ref mirror of the map so dataFetch filters against the latest resolved set even if the table
  // holds a stale closure of it.
  const mainImageInfoMapRef = useRef(mainImageInfoMap)
  // Ids requested but not yet resolved → render a loading skeleton (distinct from "no image").
  const [pendingImageIds, setPendingImageIds] = useState(new Set())

  useEffect(() => {
    mainImageInfoMapRef.current = mainImageInfoMap
    // Drop from "pending" every id that has already been resolved into the map.
    setPendingImageIds((previous) => {
      if (previous.size === 0) {
        return previous
      }
      const next = new Set([...previous].filter((id) => !(id in mainImageInfoMap)))
      return next.size === previous.size ? previous : next
    })
  }, [mainImageInfoMap])

  useEffect(() => {
    const loadAllowedOrderStatuses = async () => {
      const statuses = await apiGet('simpleOrderStatuses')
      setAllowedOrderStatuses(statuses || [])
    }
    loadAllowedOrderStatuses()
  }, [])

  useEffect(() => {
    const checkServiceForm = async () => {
      try {
        const result = await apiGet('checkForServiceForm')
        setShowServiceFormButton(result)
      } catch (err) {
        console.error('Error checking service form:', err)
        setShowServiceFormButton(false)
      }
    }
    checkServiceForm()
  }, [])

  useEffect(() => {
    const fetchForData = async () => {
      const data = await apiGet('checkForOrderData')
      setForOrder(data)
    }
    fetchForData()
  }, [])
  /**
   * Renders a list of material values (StandardSelection { id, code, name }) as wrapping chips.
   * @param {Array<{id: number, code: string, name: string}>} values Distinct material values of the order
   */
  const renderMaterialChips = (values, domain) => {
    if (!values || values.length === 0) {
      return ''
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {values.map((value) => (
          <Chip key={value.id} label={systemValueLabel(t_, domain, value)} size='small' />
        ))}
      </Box>
    )
  }

  const columns = [
    {
      name: t_('Code'),
      field: 'code',
      charLimit: 60,
      mandatory: true,
      unique: true,
      readOnly: true
    },
    {
      name: t_('Line order'),
      field: 'lineOrderQuantity',
      readOnly: true,
      defaultNewValue: null,
      sortable: false,
      search: false,
      disableFilter: true,
      render: (row) => {
        return (
          <>
            <Stack direction='row' alignItems='center'>
              {row.lineOrderQuantity}
              <IconButton
                color='primary'
                onClick={() => {
                  smartAccordionVisibility.setAccordion(row.id, true, true)
                }}
                disabled={(editing || row['smart-table-index'] === -1) || (smartAccordionVisibility.getSmartAccordionAddMode(row.id)) || smartAccordionVisibility.getSmartAccordionVisibility(row.id) || !isUserAuthorized('ADD_ORDERS_LINES')}
              >
                <AddCircleRounded />
              </IconButton>
            </Stack>
            {editIndex > -2 ? <FormHelperText> </FormHelperText> : null}
          </>
        )
      }
    },
    {
      name: t_('Main image'),
      field: 'mainImage',
      width: MAIN_IMAGE_COLUMN_WIDTH,
      readOnly: true,
      disableFilter: true,
      search: false,
      sortable: false,
      render: (row) => {
        // Only single-line orders map to exactly one part reference → one image.
        if (row.lineOrderQuantity !== 1) {
          return null
        }
        const partReferenceId = row.partReferences?.[0]?.id
        if (partReferenceId === undefined || partReferenceId === null) {
          return null
        }
        // Loading state: id requested but the batch has not resolved it yet.
        if (!(partReferenceId in mainImageInfoMap) && pendingImageIds.has(partReferenceId)) {
          return <Skeleton variant='rounded' width={MAIN_IMAGE_THUMBNAIL_WIDTH} height={MAIN_IMAGE_THUMBNAIL_HEIGHT} />
        }
        // Resolved (or unknown): MainImageThumbnail shows the image or its own "no image" placeholder.
        return (
          <MainImageThumbnail
            partReferenceId={partReferenceId}
            mainImageInfo={mainImageInfoMap[partReferenceId] || {}}
            thumbnail={mainImageThumbnailMap[partReferenceId] || ''}
          />
        )
      }
    },
    {
      name: t_('Order date'),
      field: 'date',
      mandatory: true,
      numeric: true,
      type: 'dateTimePicker',
      defaultNewValue: new Date().toISOString()
    },
    {
      name: t_('Status'),
      field: 'status.name',
      mandatory: true,
      type: 'smartSelect',
      // The status is the ONLY field the status permissions still gate (#2819): a row whose current
      // status the profile does not own stays editable, but its status cannot be changed. Creation is
      // exempt — a row with no status yet gets the normal dropdown, already filtered by the backend.
      readOnly: (row) => !!row?.status && !isOrderStatusAllowed(row.status, allowedOrderStatuses),
      // Story 204 — S72: shown, filtered and edited by code (RN2.1); the value sent and stored is still the name.
      render: (row) => systemValueLabel(t_, 'order_status', row.status),
      renderOnlyInReadMode: true,
      filterByLabel: { options: (items) => items.map((item) => ({ value: item.name, label: systemValueLabel(t_, 'order_status', item) })) },
      edit: {
        source: async () => {
          return await apiGet('simpleOrderStatuses')
        },
        id: 'id',
        field: 'name',
        optionLabel: (name, items) => systemValueOptionText(t_, 'order_status', items, name)
      }
    },
    {
      name: t_('Client'),
      field: 'client.code',
      type: 'smartSelect',
      autoSelectFirst: false,
      edit: {
        source: async () => {
          return await apiGet('clientsBasicData')
        },
        id: 'id',
        field: 'code'
      }
    },
    {
      name: t_('Target date'),
      field: 'targetDate',
      numeric: true,
      type: 'dateTimePicker',
      defaultNewValue: ''
    },
    {
      // Order-level, multi-valued: the distinct material types across ALL the order's line references,
      // shown as chips. `field` is the entity join path so the header search filters orders server-side
      // by any line's material type (contains). Read-only/derived and not sortable (a collection path
      // cannot be server-side sorted); `searchable` keeps the search box active despite sortable:false.
      name: t_('Material type'),
      field: 'lineOrders.partReference.materialType.name',
      readOnly: () => true,
      sortable: false,
      searchable: true,
      disableGenericSearch: true,
      render: (row) => renderMaterialChips(row.materialTypes, 'material_type')
    },
    {
      name: t_('Material class'),
      field: 'lineOrders.partReference.materialType.materialGroup.materialClass.name',
      readOnly: () => true,
      sortable: false,
      searchable: true,
      disableGenericSearch: true,
      render: (row) => renderMaterialChips(row.materialClasses, 'material_class')
    },
    {
      name: t_('Delivery date'),
      field: 'deliveryDate',
      numeric: true,
      type: 'dateTimePicker',
      defaultNewValue: ''
    },
    {
      name: t_('Forecast'),
      field: 'forecast',
      type: 'boolean',
      search: false,
      defaultNewValue: false
    },
    {
      name: t_('Forecast Expir. Date'),
      field: 'expirationDate',
      type: 'datePicker'
    }
  ]

  // Columns shown only for "FOR" orders: main image + the aggregated material type/class.
  const forOrderOnlyFields = [
    'mainImage',
    'lineOrders.partReference.materialType.name',
    'lineOrders.partReference.materialType.materialGroup.materialClass.name'
  ]

  const getColumns = () => {
    const visibleColumns = forOrder
      ? columns
      : columns.filter((column) => !forOrderOnlyFields.includes(column.field))
    if (extendedColumns?.length === 0) {
      return visibleColumns
    }
    return visibleColumns.concat(extendedColumns)
  }

  const dataFetch = async (pagination) => {
    const orders = await apiGet('orders', pagination)
    const rows = Array.isArray(orders) ? orders : (orders?.content || [])
    // Distinct part reference ids of the page's single-line orders (dedup before requesting).
    const distinctIds = [...new Set(
      rows
        .filter((row) => row.lineOrderQuantity === 1)
        .map((row) => row.partReferences?.[0]?.id)
        .filter((id) => id !== undefined && id !== null)
    )]
    // Cache by id: only request references not resolved yet, so paging back never re-fetches.
    const idsToResolve = distinctIds.filter((id) => !(id in mainImageInfoMapRef.current))
    if (idsToResolve.length > 0) {
      setPendingImageIds((previous) => new Set([...previous, ...idsToResolve]))
      // Single batch call for the new ids; merges into the cache without dropping resolved ones.
      mergeMainImages(idsToResolve)
    }
    return orders
  }

  const actions = {
    edit: {
      fetchFunction: (row) => {
        return apiPut('orders', row)
      },
      disabled: !isUserAuthorized('EDIT_ORDERS')
    },
    create: {
      fetchFunction: (row) => {
        if (row.client != null && Object.keys(row.client).length === 0) {
          row.client = null
        }
        return apiPost('orders', row)
      },
      disabled: !isUserAuthorized('ADD_ORDERS')
    },
    delete: {
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('orders/' + rows[0].id, rows)
        } else {
          return apiDelete('ordersMulti', rows)
        }
      },
      disabled: !isUserAuthorized('DELETE_ORDERS')
    },
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        onClick: (row) => {
          const data = { id: row.id, code: row.code, name: row.code, sensitive: row.sensitive === true }
          setOrderRow(data)
          setShowDocument(true)
        },
        disabled: editing || !isUserAuthorized('DOCUMENTS_ORDER')
      },
      {
        name: t_('Service form'),
        render: (row) => {
          return (
            <IconButton
              disabled={
                editing ||
                !isUserAuthorized('DOCUMENTS_ORDER') ||
                !(row.lineOrderQuantity > 0)
              }
            >
              <UploadFile />
            </IconButton>
          )
        },
        onClick: (row) => {
          setOrderRow(row)
          setShowServiceForm(true)
        },
        disabled: (row) =>
          editing ||
          !isUserAuthorized('DOCUMENTS_ORDER') ||
          !(row.lineOrderQuantity > 0),
        visible: showServiceFormButton
      },
      {
        name: 'Generate QR',
        render: (row) => (
          <IconButton>
            <QrCode2Icon />
          </IconButton>
        ),
        disabled: editing || !isUserAuthorized('GENERATE_QR_CODE'),
        onClick: (row) => {
          setQrRow(row)
          setShowQr(true)
        }
      }
    ],
    accordion: {
      render: (row) => (
        <LineOrder
          addModeByDefault={smartAccordionVisibility.getSmartAccordionAddMode(row.id)}
          order={row}
          preselectedRecord={childPreselectedRecordForRow(repositorySelection, row.id)}
        />
      ),
      disabled: !isUserAuthorized('SHOW_ORDERS_LINES')
    }

  }

  const validator = (row) => {
    const orderDate = moment(row.date, moment.ISO_8601, true)
    const targetDate = moment(row.targetDate, moment.ISO_8601, true)

    // V3: target date must be after order date (target date and delivery date are independent)
    if (targetDate.isValid() && orderDate.isValid()) {
      if (targetDate.isBefore(orderDate)) {
        return { errorFields: ['targetDate'], alertMessage: t_('Delivery date must be later than the order date') }
      }
    }

    return false
  }

  if (extendedColumns === null || extendedColumns === undefined) {
    return <CircularProgress />
  }
  const onAddElementClick = () => {
    if (forOrder) {
      setOpenForm(true)
    }
  }
  return (
    <>
      <SmartDialog
        closeCallback={() => setOpenForm(false)}
        title={createDialogTitle({ color: 'primary', title: t_('SOLICITUD DE FABRICACIÓN'), icon: FeedIcon })}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        contentSx={{ paddingTop: 0 }}
        renderComponent={<GeneralRequestForm setOpenForm={setOpenForm} />}
        setOpen={openForm}
      />

      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<SensitiveBoundary sensitive={orderRow?.sensitive === true}><Document reference={orderRow} resourceType='ORDER' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} /></SensitiveBoundary>}
        setOpen={showDocument}
      />
      <SmartDialog
        closeCallback={() => setShowServiceForm(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        title={createDialogTitle({ color: 'primary', title: t_('SERVICE REQUEST'), icon: FeedIcon })}
        renderComponent={
          <ServiceForm
            orderRow={orderRow}
            onClose={() => setShowServiceForm(false)}
          />
  }
        setOpen={showServiceForm}
      />
      {showQr && (
        <QrCodeGeneratorDialog
          open={showQr}
          onClose={() => setShowQr(false)}
          entityType='Order'
          entityLabel={t_('Order')}
          instance={qrRow}
          availableFields={buildAvailableFields(getColumns(), qrRow, {
            exclude: [
              'mainImage',
              'lineOrders.partReference.materialType.name',
              'lineOrders.partReference.materialType.materialGroup.materialClass.name'
            ]
          })}
        />
      )}
      <SmartMateriaUITable
        tableTuneUniqueKey='orders'
        rowPlaceholder={protectedRowPlaceholder}
        disableSelectAllData={visionCoversSensitive}
        columns={getColumns()}
        title={t_('Orders')}
        dataFetch={dataFetch}
        actions={actions}
        multipleSelection
        pageable
        preselectedRecord={parentPreselectedRecordFrom(repositorySelection)}
        onPreselectionResolved={handleOrderPreselectionResolved}
        importFile={[
          {
            bean: {
              beanName: 'ImportOrderLineOrder',
              beanPackage: 'com.smartpm.sun.usecase.order.infrastructure.importer',
              serviceName: 'ImportOrderLineOrderService',
              servicePackage: 'com.smartpm.sun.usecase.order.infrastructure.importer'
            },
            name: t_('Order and Line')
          },
          {
            bean: {
              beanName: 'FabricationOrderImportDto',
              beanPackage: 'com.smartpm.sun.usecase.fabricationorderimport.infrastructure.rest',
              serviceName: 'FabricationOrderImportService',
              servicePackage: 'com.smartpm.sun.usecase.fabricationorderimport.application'
            },
            name: t_('Fabrication Order')
          }
        ]}
        validator={validator}
        editingRow={(event) => setEditing(event)}
        accordionVisibility={smartAccordionVisibility}
        onEditIndexChanged={(editIndex) => setEditIndex(editIndex)}
        importFileDisabled={!isUserAuthorized('IMPORT_FILE_ORDERS')}
        onAddElementClick={onAddElementClick}
        customAddRecordClick={forOrder}
      />
    </>
  )
}

export default Order
